/**
 * Runtime configuration loaded from /config.json at startup.
 *
 * This decouples environment-specific values from the build,
 * enabling a single frontend bundle for all environments (12-factor).
 */

interface AppConfig {
  apiBaseUrl: string
}

let _config: AppConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api/v1',
}

let _configPromise: Promise<void> | null = null

/**
 * Fetch /config.json and store the result. Idempotent — concurrent or
 * repeat calls share the same in-flight/settled promise instead of
 * re-fetching. Falls back to defaults if the fetch fails.
 */
export function loadConfig(): Promise<void> {
  if (!_configPromise) {
    _configPromise = (async () => {
      try {
        const res = await fetch('/config.json')
        if (res.ok) {
          const json = await res.json()
          _config = { ..._config, ...json }
        }
      } catch {
        // Fall back to defaults — local dev without config.json
      }
    })()
  }
  return _configPromise
}

/** Return the current runtime config. Only valid after loadConfig() resolves. */
export function getConfig(): AppConfig {
  return _config
}

/**
 * Resolves once the runtime config has settled. Safe to call even if
 * `loadConfig()` hasn't run yet — it triggers the load itself, so any
 * caller (including ones outside `main.ts`'s bootstrap sequence) is
 * guaranteed to see the resolved `apiBaseUrl` rather than the relative
 * default.
 */
export function configReady(): Promise<void> {
  return loadConfig()
}
